/**
 * ImportDraftModal — bring an existing .txt/.md draft into ScribbleReads.
 * Upload -> Review -> Import -> straight into the editor.
 */

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, FileText, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMyWriting } from './context/MyWritingContext';
import { IMPORT_ACCEPTED_TYPES } from './constants';

function UploadStep({ onFileRead }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');

  const readFile = (file) => {
    const isAccepted = IMPORT_ACCEPTED_TYPES.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!isAccepted) {
      setError('Only .txt and .md files are supported right now.');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const guessedTitle = file.name.replace(/\.(txt|md)$/i, '').replace(/[-_]+/g, ' ');
      onFileRead({ title: guessedTitle, text });
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-coral/12 text-coral"><UploadCloud className="h-4 w-4" /></span>
        <h2 className="font-display text-xl font-semibold text-ink">Bring your story with you.</h2>
      </div>
      <p className="mt-2 text-sm text-subtle">Upload a .txt or .md file and we'll turn it into your first chapter.</p>

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) readFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        className={cx(
          'mt-5 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors',
          isDragging ? 'border-coral bg-coral/5' : 'border-line hover:border-coral/40'
        )}
        data-testid="my-writing-import-dropzone"
      >
        <FileText className="h-8 w-8 text-subtle" />
        <p className="mt-3 text-sm font-medium text-ink">Drop your file here, or click to browse</p>
        <p className="mt-1 text-xs text-subtle">.txt or .md · .docx support coming soon</p>
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.md"
          className="hidden"
          onChange={(e) => e.target.files[0] && readFile(e.target.files[0])}
        />
      </div>
      {error && <p className="mt-3 text-xs font-medium text-coral">{error}</p>}
    </div>
  );
}

function ReviewStep({ draft, onBack, onImport }) {
  const [title, setTitle] = useState(draft.title);
  const wordCount = draft.text.trim() ? draft.text.trim().split(/\s+/).length : 0;

  return (
    <div>
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs font-medium text-subtle hover:text-coral">
        <ArrowLeft className="h-3.5 w-3.5" /> Choose a different file
      </button>

      <h2 className="mt-3 font-display text-xl font-semibold text-ink">Looks good?</h2>
      <p className="mt-1 text-sm text-subtle">{wordCount.toLocaleString()} words imported. Give it a title before you continue.</p>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Story title</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 rounded-xl border-line bg-cream" />
      </div>

      <div className="mt-4 max-h-40 overflow-y-auto rounded-xl border border-line bg-cream/60 p-3 text-xs leading-relaxed text-subtle">
        {draft.text.slice(0, 600)}{draft.text.length > 600 && '…'}
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="primary" icon={false} onClick={() => onImport(title)} data-testid="my-writing-import-confirm">
          Import & Continue Writing
        </Button>
      </div>
    </div>
  );
}

function ModalBody({ onClose }) {
  const navigate = useNavigate();
  const { importStory } = useMyWriting();
  const [draft, setDraft] = useState(null);

  const handleImport = async (title) => {
    const paragraphs = draft.text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    const content = paragraphs.map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
    try {
      const story = await importStory({ title, content });
      onClose();
      navigate(`/app/studio/${story.id}`);
    } catch (error) {
      toast(error.message || 'We could not import your manuscript.');
    }
  };

  return draft
    ? <ReviewStep draft={draft} onBack={() => setDraft(null)} onImport={handleImport} />
    : <UploadStep onFileRead={setDraft} />;
}

export default function ImportDraftModal({ isOpen, onClose }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="w-[92vw] max-w-lg rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Import a draft</DialogTitle>
          <ModalBody onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Import a draft</DrawerTitle>
        <ModalBody onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
